/**
 * The five citation styles, composed from the record's own fields.
 *
 * A citation is the thing a library judges the product on, so it is composed
 * from what the archive actually holds (page, columns, date, permalink) and
 * never from a template with gaps in it. Where the API has already formatted a
 * style, that formatting wins: the server is the one that knows the
 * publisher's own imprint.
 *
 * A clipping's citation is frozen at creation. Later enrichment may improve a
 * byline or a headline, and it must not change what a footnote already claims.
 */

export const CITATION_STYLES = ['mla', 'chicago', 'apa', 'bibtex', 'ris'] as const
export type CitationStyle = (typeof CITATION_STYLES)[number]

export const CITATION_LABELS: Record<CitationStyle, string> = {
  mla: 'MLA 9',
  chicago: 'Chicago',
  apa: 'APA',
  bibtex: 'BibTeX',
  ris: 'RIS',
}

export interface CitationFacts {
  author?: string | null
  headline?: string | null
  publication?: string | null
  place?: string | null
  publisher?: string | null
  date?: string | null
  pageLabel?: string | null
  columns?: string | null
  permalink?: string | null
  accessed?: string | null
  wireService?: string | null
  productName?: string | null
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

/** MLA abbreviates every month but May, June and July. */
const MLA_MONTHS = [
  'Jan.',
  'Feb.',
  'Mar.',
  'Apr.',
  'May',
  'June',
  'July',
  'Aug.',
  'Sept.',
  'Oct.',
  'Nov.',
  'Dec.',
]

function parts(date: string | null | undefined) {
  if (!date) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date)
  if (!match) return null
  return { year: match[1], month: Number(match[2]) - 1, day: String(Number(match[3])) }
}

function mlaDate(date: string | null | undefined): string {
  const p = parts(date)
  return p ? `${p.day} ${MLA_MONTHS[p.month]} ${p.year}` : (date ?? 'n.d.')
}

function longDate(date: string | null | undefined): string {
  const p = parts(date)
  return p ? `${MONTHS[p.month]} ${p.day}, ${p.year}` : (date ?? 'n.d.')
}

function apaDate(date: string | null | undefined): string {
  const p = parts(date)
  return p ? `${p.year}, ${MONTHS[p.month]} ${p.day}` : (date ?? 'n.d.')
}

function tidyAuthor(author: string | null | undefined): string {
  return (author ?? '').replace(/^by\s+/i, '').trim()
}

/** "cols. 2-3" from a "2-3 of 5" columns label; a single column stays singular. */
function columnsPhrase(columns: string | null | undefined): string {
  if (!columns || columns === '-') return ''
  const span = columns.split(' of ')[0]
  return span.includes('–') || span.includes('-') ? `cols. ${span}` : `col. ${span}`
}

function pagePhrase(pageLabel: string | null | undefined): string {
  if (!pageLabel || pageLabel === '-') return ''
  return `p. ${pageLabel}`
}

function key(facts: CitationFacts): string {
  const stem = (facts.author || facts.publication || 'source')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
    .slice(0, 12)
  return `${stem}${parts(facts.date)?.year ?? ''}`
}

export function formatCitation(style: CitationStyle, facts: CitationFacts): string {
  const author = tidyAuthor(facts.author)
  const title = facts.headline?.trim() || 'Untitled'
  const publication = facts.publication ?? ''
  const place = facts.place ? ` (${facts.place})` : ''
  const page = pagePhrase(facts.pageLabel)
  const cols = columnsPhrase(facts.columns)
  const where = [page, cols].filter(Boolean).join(', ')
  const link = facts.permalink ?? ''
  const product = facts.productName ?? ''
  const wire = facts.wireService ? `${facts.wireService}. ` : ''

  switch (style) {
    case 'mla':
      return [
        author ? `${author}. ` : '',
        `“${title}.” `,
        `${publication}${place}, ${mlaDate(facts.date)}`,
        where ? `, ${where}` : '',
        '. ',
        product ? `${product}, ` : '',
        link,
        '.',
      ].join('')
    case 'chicago':
      return [
        author ? `${author}. ` : '',
        `“${title}.” `,
        `${publication}${place}, ${longDate(facts.date)}`,
        where ? `, ${where}` : '',
        '. ',
        link,
        '.',
      ].join('')
    case 'apa':
      return [
        author ? `${author}. ` : '',
        `(${apaDate(facts.date)}). `,
        `${title}. `,
        `${publication}`,
        where ? `, ${where}` : '',
        '. ',
        link,
      ].join('')
    case 'bibtex':
      return [
        `@article{${key(facts)},`,
        `  author    = {${author}},`,
        `  title     = {${title}},`,
        `  journal   = {${publication}},`,
        `  date      = {${facts.date ?? ''}},`,
        `  pages     = {${facts.pageLabel ?? ''}},`,
        facts.columns && facts.columns !== '-' ? `  note      = {${facts.columns}},` : '',
        `  url       = {${link}},`,
        `  urldate   = {${facts.accessed ?? ''}}`,
        '}',
      ]
        .filter(Boolean)
        .join('\n')
    case 'ris':
      return [
        'TY  - NEWS',
        author ? `AU  - ${author}` : '',
        `TI  - ${title}`,
        `JO  - ${publication}`,
        facts.place ? `CY  - ${facts.place}` : '',
        `DA  - ${facts.date ?? ''}`,
        facts.pageLabel ? `SP  - ${facts.pageLabel}` : '',
        wire ? `N1  - ${wire.trim()}` : '',
        `UR  - ${link}`,
        'ER  -',
      ]
        .filter(Boolean)
        .join('\n')
  }
}

/**
 * The style the reader asked for, preferring what the server formatted. The
 * API's citation object carries either the five formatted styles or the
 * fields they are composed from, and both shapes have to work here.
 */
export function citationFor(
  style: CitationStyle,
  provided: Record<string, string | null> | null | undefined,
  facts: CitationFacts,
): string {
  const server = provided?.[style]
  if (typeof server === 'string' && server.trim()) return server
  return formatCitation(style, facts)
}

/** The field dict the API sends today, mapped onto the facts above. */
export function factsFromCitation(
  provided: Record<string, string | null> | null | undefined,
  extra: CitationFacts = {},
): CitationFacts {
  return {
    author: extra.author ?? provided?.author ?? null,
    headline: extra.headline ?? provided?.headline ?? null,
    publication: extra.publication ?? provided?.publication ?? null,
    place: extra.place ?? provided?.place ?? null,
    publisher: extra.publisher ?? provided?.publisher ?? null,
    date: extra.date ?? provided?.date ?? null,
    pageLabel: extra.pageLabel ?? provided?.page ?? null,
    columns: extra.columns ?? null,
    permalink: extra.permalink ?? provided?.permalink ?? null,
    accessed: extra.accessed ?? provided?.accessed ?? null,
    wireService: extra.wireService ?? provided?.wire_service ?? null,
    productName: extra.productName ?? null,
  }
}
